/**
 * Where a chosen theme is remembered, and how it is restored before first paint.
 *
 * ── What the choice actually is ─────────────────────────────────────────────
 *
 * NOT a theme, in the sense of a palette to load: `globals.css` carries both
 * fields in `light-dark()` and swaps them off `color-scheme`, so all that is
 * stored here is which one to force. Absent a stored value, nothing is forced
 * and the reader's operating system decides — which is what the site did before
 * this control existed, and is why a browser set to dark and a browser set to
 * light show different defaults. That is correct behaviour; the switch exists
 * for the reader who disagrees with their own OS setting on this one site.
 *
 * ── Why a plain module, and not part of `ThemeToggle.tsx` ───────────────────
 *
 * The same reason `navScope.ts` is one: every export of a `'use client'` file is
 * a client reference the server may render but never CALL, and `themeBootScript`
 * has to be called on the server to produce a string that goes into the layout's
 * markup. Behind `'use client'` it would throw
 *
 *     Attempted to call themeBootScript() from the server but themeBootScript is
 *     on the client.
 *
 * The DOM helpers stay in the component, where the DOM exists.
 */
export type Theme = 'light' | 'dark'

/** The attribute CSS matches on: `data-theme` on <html>. */
export const themeAttribute = 'data-theme'

/** Where the choice is kept between visits. Namespaced like `nexora.*.nav`. */
export const themeStorageKey = 'nexora.theme'

/**
 * The script that applies a stored choice before anything paints.
 *
 * Inlined into `<head>`, never fetched, for the reason the sidebar's boot script
 * gives at length: a request cannot beat the first paint, and this one has to.
 * Without it a reader who has chosen light gets a black page for as long as the
 * client bundle takes to arrive — the whole screen, on every navigation, in the
 * one direction the eye cannot ignore.
 *
 * It writes nothing when nothing is stored, so the system preference is left to
 * do its job. Anything other than the two known values is ignored rather than
 * trusted: this reads from storage the page shares with every other script on
 * the origin.
 */
export const themeBootScript = (): string =>
  `try{var t=localStorage.getItem('${themeStorageKey}');if(t==='light'||t==='dark'){document.documentElement.setAttribute('${themeAttribute}',t)}}catch(e){}`
